/* eslint-disable no-unused-vars */
import DataTable from "../components/common/DataTable";
import HeaderInfo from "../components/common/HeaderInfo";
import { EyeIcon, Filter, Search } from "../components/icons";
import itmImg from "../assets/item-thumbnail.png";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import InitializeStockModal from "../components/modals/InitializeStockModal";
import StockAdjustmentModal from "../components/modals/StockAdjustmentModal";
import CentralInventoryDetailsModal from "../components/modals/CentralInventoryDetailsModal";
import LowStockModal from "../components/modals/LowStockModal";
import {
  useAdjustCentralInventoryStockMutation,
  useGetCentralInventoryByIdQuery,
  useGetCentralInventoryQuery,
  useGetCategoriesQuery,
} from "../store/api";

export default function CentralInventory() {
  const [initializeOpen, setInitializeOpen] = useState(false);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [lowStockOpen, setLowStockOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedCategory, limit]);

  const { data: categoriesData } = useGetCategoriesQuery();
  const categories = categoriesData?.data?.categories || [];

  const [adjustStock, { isLoading: isAdjusting }] =
    useAdjustCentralInventoryStockMutation();

  // Fetch central inventory data
  const { data, isLoading, isFetching, error } = useGetCentralInventoryQuery({
    page,
    limit,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(selectedCategory && { category: selectedCategory }),
  });

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || "Failed to load central inventory");
    }
  }, [error]);

  const totalPages = data?.data?.totalPages || 1;

  function handleNext() {
    if (page < totalPages) setPage((p) => p + 1);
  }

  function handlePrev() {
    if (page > 1) setPage((p) => p - 1);
  }

  const { data: selectedItemDetails, isLoading: isLoadingDetails } =
    useGetCentralInventoryByIdQuery(selectedItemId, {
      skip: !selectedItemId,
    });



  function handleOpenAdd() {
    setInitializeOpen(true);
  }

  function handleCloseModal() {
    setInitializeOpen(false);
  }

  function handleOpenAdjustment(row) {
    setSelectedItem(row);
    setAdjustmentOpen(true);
  }

  function handleOpenDetails(row) {
    setSelectedItemId(row?.id || row?._id || null);
    setSelectedItem(row);
    setDetailsOpen(true);
  }

  function handleCloseDetails() {
    setDetailsOpen(false);
    setSelectedItemId(null);
    setSelectedItem(null);
  }

  const apiRows = data?.data?.items || [];

  const states = [
    {
      label: "Total Items",
      value: apiRows.length,
      desc: "Cataloged Products",
      bg: "#E2E2FF8C",
    },
    {
      label: "Total Stock",
      value: apiRows?.reduce(
        (total, item) => total + Number(item.quantity || 0),
        0,
      ),
      desc: "Units in Hand",
      bg: "#6D4E8AD4",
    },
    {
      label: "Central Branches",
      value: 1,
      desc: "Active Warehouses",
      bg: "#E2E2FF8C",
    },
  ];
  const columns = [
    { key: "productName", label: "Product Name", className: "col-span-6" },
    {
      key: "stock",
      label: "Available Stock",
      className: "col-span-3 text-center",
    },
    { key: "actions", label: "Actions", className: "col-span-3 text-center" },
  ];
 
  const renderRow = (r, idx) => (
    <div
      key={idx}
      className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5] bg-[#EEEEEE40]"
    >
      <div className="col-span-6 flex items-center gap-[14px] text-black">
        <div className="w-[53px] h-[42px]">
          <img
            src={r?.itemImage || itmImg}
            alt="item"
            className="w-full h-full object-cover"
          />
        </div>

        <p className="text-black">{r.itemName}</p>
      </div>

      <div className="col-span-3 text-center">
        <div className="inline-block bg-[#E5FFF6] text-[#2C765C] px-2.5 py-1 rounded-full text-xs">
          {r.quantity} Units
        </div>
      </div>

      <div className="col-span-3 flex justify-center gap-4">
        <button
          aria-label="view details"
          className="cursor-pointer"
          onClick={() => handleOpenDetails(r)}
        >
          <EyeIcon />
        </button>
        <button
          aria-label="adjust stock"
          onClick={() => handleOpenAdjustment(r)}
          className="text-xs leading-6 text-white px-2 rounded-full bg-[#6D4E8A]"
        >
          Adjust Stock 
        </button>
      </div>
    </div>
  );
  return (
    <div className="pt-8 xl:pt-14 pl-8 pr-12 min-h-screen">
      <HeaderInfo
        title="Central Inventory"
        desc="Real-time stock management across central warehouses"
        btnLabel="Initialize Stock"
        onAdd={handleOpenAdd}
        searchPlaceholder="Search catalog by name or id.."
        extraButtons={
          <button
            onClick={() => setLowStockOpen(true)}
            className="rounded-[10px] text-sm lg:text-base bg-[#F0EDF5] text-[#6D4E8A] font-medium py-2 lg:py-2.5 px-6 lg:px-[30.5px] transition hover:bg-[#e4dfed]"
          >
            View Low Stock
          </button>
        }
      />
      <div className="grid grid-cols-3 gap-3 mt-6">
        {states.map((stat, idx) => (
          <div
            key={idx}
            style={{ backgroundColor: stat.bg }}
            className={`${idx === 1 ? "text-white" : "text-black"} shadow-[0_2px_4px_0_#00000040]   rounded-[20px] pt-6 pb-4 px-8`}
          >
            <h3 className="text-xl font-bold leading-[31px]">{stat.label}</h3>
            <p className="text-4xl  font-bold leading-[31px]">{stat.value}</p>
            <p className="leading-[31px]">{stat.desc}</p>
          </div>
        ))}
      </div>
      <div className="rounded-[10px] border border-[#D9D9D9] mt-6 pt-6 pb-4">
        <div className="pl-5 pr-6 mb-4">
          <div className=" border border-[#D9D9D9] rounded-[10px] py-4 pl-6 pr-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center flex-1 min-w-[250px] gap-2 py-3 px-4 bg-[#EEEEEE40] rounded-[10px]">
              <Search />
              <input
                type="text"
                placeholder="Search catalog by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent outline-none text-black placeholder:text-[#878686]"
              />
            </div>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm min-w-[200px] capitalize"
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c._id} value={c._id} className="capitalize">{c.name}</option>
              ))}
            </select>

            <button
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
                setSelectedCategory("");
                setPage(1);
              }}
              className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Reset Filters
            </button>
          </div>
        </div>

        <div className="mt-2 border-t border-[#E5E5E5] pt-4">
          <DataTable
            columns={columns}
            rows={apiRows}
            renderRow={renderRow}
            page={page}
            totalPages={totalPages}
            onNext={handleNext}
            onPrev={handlePrev}
            isLoading={isLoading || isFetching}
          />
        </div>
      </div>

      <LowStockModal
        open={lowStockOpen}
        onClose={() => setLowStockOpen(false)}
      />
      <InitializeStockModal
        open={initializeOpen}
        onClose={handleCloseModal}
       
      />
      <CentralInventoryDetailsModal
        open={detailsOpen}
        onClose={handleCloseDetails}
        inventoryItem={selectedItemDetails?.data || selectedItem || {}}
        isLoading={isLoadingDetails}
      />
      <StockAdjustmentModal
        open={adjustmentOpen}
        onClose={() => {
          setAdjustmentOpen(false);
          setSelectedItem(null);
        }}
        productName={selectedItem?.itemName || ""}
        currentStock={parseInt(selectedItem?.quantity) || 0}
        isLoading={isAdjusting}
        onSubmit={async (adjustment) => {
          try {
            const response = await adjustStock({
              id: selectedItem._id || selectedItem.id,
              action:
                adjustment.mode === "add"
                  ? "increase"
                  : "decrease",
              amount: adjustment.amount,
            }).unwrap();
        
            if (response?.success === false) {
              toast.error(response?.message || "Failed to adjust stock");
              return;
            }
            toast.success("Stock adjusted successfully");
            setAdjustmentOpen(false);
            setSelectedItem(null);
          } catch (err) {
            toast.error(err?.data?.message || err.message || "Failed to adjust stock");
          }
        }}
      />
    </div>
  );
}
